import React, { FC, useContext, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useTour } from '@reactour/tour';
import Button, { IButtonProps } from 'components/bootstrap/Button';
import OffCanvas, {
	OffCanvasBody,
	OffCanvasHeader,
	OffCanvasTitle,
} from 'components/bootstrap/OffCanvas';
import Alert from 'components/bootstrap/Alert';
import { HeaderRight } from 'layout/Header/Header';
import Icon from 'components/icon/Icon';
import ThemeContext from 'contexts/themeContext';
import useDarkMode from 'hooks/useDarkMode';
import Popovers from 'components/bootstrap/Popovers';
import Badge from 'components/bootstrap/Badge';
import { useSelector } from 'react-redux';
import { RootState } from 'stores/store';
import { selectCompanyId, selectProjectId } from 'stores/projectCompanySlice';
import { getProject } from 'helpers/helpers';
import { IdTypeEnumForSearchEnum } from 'enums';

interface CommonHeaderRightProps {
	beforeChildren?: any;
	afterChildren: any;
}

const CommonHeaderRight: FC<CommonHeaderRightProps> = ({ beforeChildren, afterChildren }) => {
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();

	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const styledBtn: IButtonProps = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	const [offcanvasStatus, setOffcanvasStatus] = useState(false);

	const { i18n } = useTranslation();

	const { user, notifications } = useSelector((state: RootState) => state.auth);
	const companyId = useSelector((state: RootState) => selectCompanyId(state));
	const projectId = useSelector((state: RootState) => selectProjectId(state));

	/**
	 * Language attribute
	 */
	useLayoutEffect(() => {
		document.documentElement.setAttribute('lang', i18n.language);
	});

	const { setIsOpen } = useTour();

	const project = () => getProject(projectId, companyId, user);
	const currentProject = project();

	return (
		<HeaderRight>
			<div className='row g-3'>
				{beforeChildren && beforeChildren}
				{currentProject && (
					<div className='col-auto' data-tour='project-state-tour'>
						{currentProject.is_live ? (
							<Badge className='fs-5 mt-2' color='success' isLight>
								You are live
								<Icon className='ms-2' icon='InfoOutline' />
							</Badge>
						) : (
							<Badge className='fs-5 mt-2' color='warning' isLight>
								You are in test mode
								<Icon className='ms-2' icon='InfoOutline' />
							</Badge>
						)}
					</div>
				)}
				{/* Tour Modal */}
				{localStorage.getItem('tourModalStarted') === 'shown' && (
					<div className='col-auto position-relative'>
						<Popovers trigger='hover' desc='Start the "System" tour'>
							<Button
								// eslint-disable-next-line react/jsx-props-no-spreading
								{...styledBtn}
								icon='Tour'
								onClick={() => setIsOpen(true)}
								aria-label='Start tour'
							/>
						</Popovers>
						<Icon
							icon='Circle'
							className={classNames(
								'position-absolute start-65',
								'text-danger',
								'animate__animated animate__heartBeat animate__infinite animate__slower',
							)}
						/>
					</div>
				)}

				{/* Dark Mode */}
				<div className='col-auto'>
					<Popovers trigger='hover' desc='Dark / Light mode'>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							onClick={() => setDarkModeStatus(!darkModeStatus)}
							className='btn-only-icon'
							data-tour='dark-mode'>
							<Icon
								icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
								color={darkModeStatus ? 'info' : 'warning'}
								className='btn-icon'
							/>
						</Button>
					</Popovers>
				</div>

				{/*	Full Screen */}
				<div className='col-auto'>
					<Popovers trigger='hover' desc='Fullscreen'>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							icon={fullScreenStatus ? 'FullscreenExit' : 'Fullscreen'}
							onClick={() => setFullScreenStatus(!fullScreenStatus)}
							aria-label='Toggle dark mode'
							data-tour='full-screen'
						/>
					</Popovers>
				</div>

				{/*	Notifications */}
				<div className='col-auto' data-tour='notification-tour'>
					<Button
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...styledBtn}
						icon='Notifications'
						onClick={() => setOffcanvasStatus(true)}
						aria-label='Notifications'
					/>
				</div>

				{afterChildren}
			</div>

			<OffCanvas
				id='notificationCanvas'
				titleId='offcanvasExampleLabel'
				placement='end'
				isOpen={offcanvasStatus}
				setOpen={setOffcanvasStatus}>
				<OffCanvasHeader setOpen={setOffcanvasStatus}>
					<OffCanvasTitle id='offcanvasExampleLabel'>Notifications</OffCanvasTitle>
				</OffCanvasHeader>
				<OffCanvasBody>
					{notifications &&
						notifications &&
						notifications.map((item: any, i: number) => {
							const namespaceString = item.notifiable_type;
							const namespaceArray = namespaceString.split('\\');
							const lastItem =
								namespaceArray[namespaceArray.length - 1].toLowerCase();
							let icon = 'AccessAlarm';
							switch (lastItem) {
								case IdTypeEnumForSearchEnum.COMPANY:
									icon = 'Tonality';
									break;
								case IdTypeEnumForSearchEnum.CURRENCY:
									icon = 'Payments';
									break;
								case IdTypeEnumForSearchEnum.CUSTOMER:
									icon = 'AccountCircle';
									break;
								case IdTypeEnumForSearchEnum.CUSTOMER_PLAN:
									icon = 'StackedBarChart';
									break;
								case IdTypeEnumForSearchEnum.PLAN:
									icon = 'AutoAwesomeMotion';
									break;
								case IdTypeEnumForSearchEnum.PRODUCT:
									icon = 'VerticalDistribute';
									break;
								case IdTypeEnumForSearchEnum.PROJECT:
									icon = 'Terrain';
									break;
								case IdTypeEnumForSearchEnum.USER:
									icon = 'Person';
									break;
								case IdTypeEnumForSearchEnum.WALLET:
									icon = 'AccountBalanceWallet';
									break;
								case IdTypeEnumForSearchEnum.ROLE:
									icon = 'AccountBalanceWallet';
									break;
							}

							return (
								<Alert
									key={item.id || i}
									icon={icon}
									isLight
									color={item.read_at ? 'light' : 'info'}
									className='flex-nowrap'>
									{item.data.title}
								</Alert>
							);
						})}
				</OffCanvasBody>
			</OffCanvas>
		</HeaderRight>
	);
};

export default CommonHeaderRight;
